import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { useAuth } from "../auth";
import { useTheme } from "../theme";
import { Icons } from "../components/icons";
import { type OrgMembership } from "../api";
import {
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useDeleteCategoryMutation,
} from "../store/categories";
import { ApiKeysSection } from "../components/ApiKeysSection";

type Tab = "cuenta" | "apariencia" | "categorias" | "ingesta" | "api-keys";

const TABS: { id: Tab; label: string; icon: keyof typeof Icons }[] = [
  { id: "cuenta",     label: "Cuenta",             icon: "Users" },
  { id: "apariencia", label: "Apariencia",         icon: "Sun" },
  { id: "categorias", label: "Categorías",         icon: "Cube" },
  { id: "api-keys",   label: "API Keys",           icon: "Shield" },
  { id: "ingesta",    label: "Ingesta de eventos", icon: "Database" },
];

export default function Me() {
  const { user, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const [tab, setTab] = useState<Tab>("cuenta");

  const contentRef = useRef<HTMLDivElement>(null);

  function switchTab(next: Tab) {
    setTab(next);
    contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (!user) return null;

  return (
    <div className="settings-shell">
      <header className="page-header">
        <div>
          <h1>Configuración</h1>
          <div className="sub">Cuenta, apariencia, organización e integración con tu app.</div>
        </div>
      </header>

      <div className="settings-layout">
        <nav className="settings-nav">
          {TABS.map((t) => {
            const I = Icons[t.icon];
            return (
              <button key={t.id} className={tab === t.id ? "active" : ""} onClick={() => switchTab(t.id)}>
                <I size={14} sw={1.7} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="settings-content" ref={contentRef}>
          {tab === "cuenta" && <AccountSection user={user} signOut={signOut} />}
          {tab === "apariencia" && <ThemeSection theme={theme} toggle={toggle} />}
          {tab === "categorias" && <CategoriesSection user={user} />}
          {tab === "api-keys" && <ApiKeysSection user={user} />}
          {tab === "ingesta" && <IngestSection />}
        </div>
      </div>
    </div>
  );
}

// ---------------- Sections ----------------

function AccountSection({
  user,
  signOut,
}: {
  user: {
    id: string;
    email: string;
    org_id: string | null;
    roles: string[];
    display_name: string | null;
    available_orgs?: OrgMembership[];
    is_platform_admin?: boolean;
  };
  signOut: () => Promise<void>;
}) {
  return (
    <>
      <SettingsCard
        title="Cuenta"
        sub="Identidad con la que estás logueado."
        badge={<span className="badge badge-ok"><span className="dot" style={{ background: "currentColor" }} /> Activa</span>}
      >
        <FieldRow label="Email" sub="Lo usás para iniciar sesión.">
          <input className="input" value={user.email} readOnly />
        </FieldRow>
        <FieldRow label="Nombre" sub="Cómo aparecés en incidentes y comentarios.">
          <input className="input" value={user.display_name ?? ""} placeholder="Sin definir" readOnly />
        </FieldRow>
        <FieldRow label="Organización" sub="Filtro aplicado a todas tus queries.">
          <input className="input" value={user.org_id ?? "Todas (rol admin)"} readOnly />
        </FieldRow>
        <FieldRow label="Roles" sub="Determinan qué acciones podés ejecutar.">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {user.roles.map((r) => (
              <span key={r} className="badge badge-muted">{r}</span>
            ))}
          </div>
        </FieldRow>
        <FieldRow label="Organizaciones" sub="Las orgs a las que pertenecés y tu rol en cada una.">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(user.available_orgs ?? []).length === 0 ? (
              <span className="text-muted text-sm">
                {user.is_platform_admin
                  ? "Sos platform admin — ves todas las orgs."
                  : "Sin organizaciones asignadas."}
              </span>
            ) : (
              (user.available_orgs ?? []).map((o) => (
                <span key={o.org_id} className="badge badge-muted">
                  {o.name} · {o.role}
                </span>
              ))
            )}
          </div>
        </FieldRow>
      </SettingsCard>

      <SettingsCard
        title="Sesión"
        sub="Tus tokens viven en localStorage. Para revocar otros dispositivos pedile al admin que rote tu password."
      >
        <FieldRow label="Cerrar sesión" sub="Borra access + refresh tokens locales y revoca el refresh en el server.">
          <button type="button" className="btn btn-danger" onClick={() => void signOut()}>
            <Icons.Logout size={13} />
            Cerrar sesión
          </button>
        </FieldRow>
      </SettingsCard>
    </>
  );
}

function ThemeSection({ theme, toggle }: { theme: string; toggle: () => void }) {
  return (
    <SettingsCard
      title="Apariencia"
      sub="Modo claro u oscuro. Tu elección se guarda en este navegador."
    >
      <FieldRow label="Tema" sub="Toggle inmediato; persiste en localStorage.">
        <button type="button" className="btn btn-secondary" onClick={toggle}>
          {theme === "dark" ? <Icons.Moon size={13} /> : <Icons.Sun size={13} />}
          {theme === "dark" ? "Oscuro" : "Claro"}
        </button>
      </FieldRow>
    </SettingsCard>
  );
}

function CategoriesSection({
  user,
}: {
  user: { roles: string[] };
}) {
  const canEdit = user.roles.includes("admin") || user.roles.includes("operator");
  // RTK Query: cache compartido (mismo endpoint usado por CategoryCombobox) +
  // invalidación automática al agregar/borrar.
  const { data: items = [], isLoading: loading } = useGetCategoriesQuery();
  const [createCategoryMutation] = useCreateCategoryMutation();
  const [deleteCategoryMutation] = useDeleteCategoryMutation();
  const [newCategory, setNewCategory] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    const normalized = newCategory.trim().toLowerCase();
    if (!normalized) return;
    if (!/^[a-z0-9._-]+$/.test(normalized)) {
      setError("Solo lowercase, números, punto, guion y guion bajo.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createCategoryMutation(normalized).unwrap();
      setNewCategory("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido");
    } finally {
      setSubmitting(false);
    }
  }

  async function onRemove(cat: string) {
    if (
      !confirm(
        `¿Eliminar categoría "${cat}"?\n\nLos servicios que ya la usan no se borran, pero deja de aparecer como sugerencia al crear nuevos.`,
      )
    ) {
      return;
    }
    try {
      await deleteCategoryMutation(cat).unwrap();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido");
    }
  }

  return (
    <SettingsCard
      title="Categorías de servicios"
      sub="Catálogo compartido por todas las organizaciones de la plataforma. Aparecen como opciones en el selector cuando creás un servicio."
    >
      {canEdit && (
        <form onSubmit={onAdd} style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          <input
            className="input"
            placeholder="nueva-categoria"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value.toLowerCase())}
            style={{ flex: 1, fontFamily: "var(--font-mono)" }}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!newCategory || submitting}
          >
            {submitting ? "Creando…" : "Agregar"}
          </button>
        </form>
      )}

      {error && (
        <div
          style={{
            color: "var(--st-major)",
            background: "color-mix(in oklab, var(--st-major) 10%, transparent)",
            border: "1px solid color-mix(in oklab, var(--st-major) 30%, transparent)",
            padding: "8px 10px",
            borderRadius: 6,
            fontSize: 12.5,
            marginBottom: 8,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: "var(--fg-3)", padding: "12px 0", fontSize: 13 }}>Cargando…</div>
      ) : items.length === 0 ? (
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            background: "var(--bg-2)",
            border: "1px dashed var(--border-1)",
            borderRadius: 6,
            color: "var(--fg-3)",
            fontSize: 13,
          }}
        >
          {canEdit
            ? "Sin categorías todavía. Creá la primera arriba."
            : "Sin categorías todavía. Pedile a un admin que cree alguna."}
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {items.map((c) => (
            <span
              key={c}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 4px 4px 10px",
                background: "var(--bg-2)",
                border: "1px solid var(--border-1)",
                borderRadius: 999,
                fontFamily: "var(--font-mono)",
                fontSize: 12.5,
              }}
            >
              {c}
              {canEdit && (
                <button
                  type="button"
                  className="btn btn-icon btn-ghost btn-sm"
                  onClick={() => onRemove(c)}
                  title={`Eliminar ${c}`}
                  style={{ padding: 2 }}
                >
                  <Icons.Close size={11} sw={2} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </SettingsCard>
  );
}

function IngestSection() {
  const apiBase =
    typeof window !== "undefined"
      ? window.location.origin.replace("5173", "4100")
      : "https://sovra-logs";

  return (
    <>
      <SettingsCard
        title="Ingesta de eventos"
        sub="Tus aplicaciones (gov-citizen, gov-functionary u otras) envían eventos firmados con HMAC a este endpoint. Cada llamada externa que querés observar genera un IngestEvent."
        badge={<span className="badge badge-ok"><span className="dot" style={{ background: "currentColor" }} /> Operativo</span>}
      >
        <SecretRow
          label="Endpoint"
          sub="Acá postean los clientes los eventos"
          value={`POST ${apiBase}/v1/ingest/events`}
        />
        <SecretRow
          label="Secret"
          sub="Compartido por canal seguro con el equipo que instrumenta los clientes"
          value="OBSERVABILITY_INGEST_SECRET (server-side)"
          type="secret"
        />
        <SecretRow
          label="Headers requeridos"
          sub="Sin alguno de los tres devuelve 401"
          value="X-Signature · X-Timestamp · X-Org-Id"
        />
        <SecretRow
          label="Tolerancia de timestamp"
          sub="Diferencia máxima entre el reloj del cliente y el servidor"
          value="±300 segundos"
        />
        <SecretRow
          label="Rate limit"
          sub="Por organización. Si lo superás devuelve 429."
          value="1000 req/min"
        />
      </SettingsCard>

      <SettingsCard
        title="Instrumentación del cliente"
        sub="Wrapper drop-in sobre fetch. Cada llamada externa emite request + response automáticamente. Si OBSERVABILITY_INGEST_SECRET no está seteado, opera como fetch normal y solo loguea warnings."
      >
        <CodeBlock>{`// Ejemplo gov-citizen — observar una llamada a Renaper

const { callExternal } = require("./sovra-logs-client");

async function consultaDni(dni) {
  const res = await callExternal(
    "https://api.renaper.gob.ar/persons/lookup",
    {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ dni }),
    },
    {
      service_id: "identidad.renaper",    // debe existir en sovra-logs
      tags:       { operation: "consulta_dni", folio: \`NL-\${Date.now()}\` },
      end_user:   { email: req.user.email },
    },
  );
  return res.json();
}`}</CodeBlock>
      </SettingsCard>
    </>
  );
}

// ---------------- Primitives ----------------

function SettingsCard({ title, sub, badge, children }: { title: string; sub?: string; badge?: ReactNode; children: ReactNode }) {
  return (
    <section className="settings-card">
      <header>
        <div>
          <h3>{title}</h3>
          {sub && <p>{sub}</p>}
          {badge && <div style={{ marginTop: 8 }}>{badge}</div>}
        </div>
      </header>
      <div className="settings-card-body">{children}</div>
    </section>
  );
}

function FieldRow({ label, sub, children }: { label: string; sub?: string; children: ReactNode }) {
  return (
    <div className="field-row">
      <div>
        <div className="fr-label">{label}</div>
        {sub && <div className="fr-sub">{sub}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function SecretRow({
  label,
  sub,
  value,
  type = "value",
}: {
  label: string;
  sub?: string;
  value: string;
  type?: "value" | "secret";
}) {
  const [revealed, setRevealed] = useState(type === "value");
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const display = revealed ? value : value.replace(/./g, "•");

  return (
    <FieldRow label={label} sub={sub}>
      <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
        <input className="input text-mono" value={display} readOnly style={{ fontSize: 12.5, flex: 1 }} />
        {type === "secret" && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRevealed((v) => !v)}>
            {revealed ? "Ocultar" : "Revelar"}
          </button>
        )}
        <button type="button" className="btn btn-secondary btn-sm" onClick={copy}>
          {copied ? "✓ Copiado" : "Copiar"}
        </button>
      </div>
    </FieldRow>
  );
}

function CodeBlock({ children }: { children: ReactNode }) {
  const [copied, setCopied] = useState(false);
  const text = typeof children === "string" ? children : "";
  async function copy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <div className="cfg-code-block">
      <button type="button" onClick={copy} className="cfg-code-copy">
        {copied ? "✓ Copiado" : "Copiar"}
      </button>
      <pre>{children}</pre>
    </div>
  );
}
